import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { formatDateList } from './CoverageWarningList';
import type { TimeOffCoverage } from '../../services/api/timeOffCoverage';

/**
 * MKS-361 — the approval queue's Coverage column.
 *
 * Compact on purpose: one line per AFFECTED project — the stand-in, or a
 * plain "No substitute" when nobody has been named on it yet — plus an amber
 * badge when a role would be left with nobody. An approver scanning twenty
 * rows needs to see "covered / not covered / clash" at a glance; the detail
 * (which days, who else is away, changing the pick) is one click away in
 * `EditSubstitutesModal`.
 *
 * Every affected project gets a line, not only the ones with a substitute:
 * listing the covered ones alone makes "covered on one project of three" look
 * exactly like "covered everywhere", which is the mistake this column exists
 * to prevent.
 */

interface TimeOffCoverageCellProps {
  coverage: TimeOffCoverage | undefined;
  /** Opens the editor. Omitted when the viewer may not change the pick. */
  onEdit?: () => void;
}

export default function TimeOffCoverageCell({ coverage, onEdit }: TimeOffCoverageCellProps) {
  const substitutes = coverage?.substitutes ?? [];
  const warnings = coverage?.warnings ?? [];

  // The affected projects, plus any substitute stored on a project that is no
  // longer among them (the dates moved under an old pick) so nothing stored
  // is silently invisible.
  const byProject = new Map(substitutes.map((s) => [s.projectId, s] as const));
  const rows = [
    ...(coverage?.projects ?? []),
    ...substitutes
      .filter((s) => !(coverage?.projects ?? []).some((p) => p.projectId === s.projectId))
      .map((s) => ({ projectId: s.projectId, projectName: s.projectName })),
  ];

  return (
    <div className="space-y-1 text-sm">
      {rows.length === 0 ? (
        <span className="text-gray-400">No substitute</span>
      ) : (
        <ul className="space-y-0.5">
          {rows.map((project) => {
            const substitute = byProject.get(project.projectId);
            return (
              <li key={project.projectId} className="text-gray-900">
                <span className="text-gray-500">{project.projectName}:</span>{' '}
                {substitute ? (
                  substitute.substituteName
                ) : (
                  <span className="text-gray-400">No substitute</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {warnings.length > 0 && (
        <div className="flex items-start gap-1 text-amber-700">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>
            {warnings.map((warning) => (
              <span key={`${warning.projectId}-${warning.roleId}`} className="block">
                {warning.projectName}
                {warning.roleName ? ` — ${warning.roleName}` : ''} uncovered{' '}
                {formatDateList(warning.dates)}
              </span>
            ))}
          </span>
        </div>
      )}

      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          {substitutes.length === 0 ? 'Set substitute' : 'Change'}
        </button>
      )}
    </div>
  );
}
